import { z } from 'zod';

export const WEBHOOK_EVENT_TYPES = [
  'audit.completed',
  'audit.failed',
  'audit.started',
  /**
   * Emitted once per `snapshot_groups` row when every child snapshot
   * has reached a terminal status (success / error / timeout). Payload
   * carries the rollup counts so subscribers can render a "run
   * finished, X/Y succeeded" line without round-tripping per snapshot.
   * Audit-config-on-sites added this and replaced the previous
   * `monitor.run.failed` (which only fired on terminal-error rollups).
   */
  'audit_group.completed',
  /**
   * Fired from `PATCH /v1/management/sites/{uuid}` on every successful
   * write. Payload carries the site uuid + the set of fields the
   * caller actually mutated, so subscribers can keep a downstream
   * cache in sync without polling. See `./site-event-payload.ts` for
   * the data shape.
   */
  'site.updated',
  // usage-alerts phase 0 — payload shape in `./usage-alerts.ts`
  // (`UsageThresholdEventDataSchema`). Envelope is unchanged.
  'usage.threshold.warning',
  'usage.threshold.critical',
  /**
   * Emitted once per `chat_conversations` row at creation time. Fires
   * for both transports (CLI auto-sync and the ChatAgent DO). Payload
   * carries the conversation uuid + the `created_via` discriminator so
   * compliance/audit-trail subscribers can ingest a stable record of
   * every chat session start. Per-message events are deliberately NOT
   * emitted — too high-volume; subscribers who want full conversation
   * content should poll the API. See `./chat-event-payload.ts`.
   */
  'chat.conversation.created',
  /**
   * Ambient analysis found something worth telling the customer.
   * Emitted once per finding at creation — NOT on the re-detections
   * that bump `occurrence_count`, or a fortnight-long regression would
   * mail them every week until it cleared. Payload shape in
   * `./insight-event-payload.ts`.
   *
   * Volume is deliberately tiny (0-3 per site per month). This is a
   * surprise-and-delight signal, not an alert: nothing is broken if a
   * subscriber ignores it, and it must never read as action-required.
   */
  'insight.detected',
] as const;
export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

// `email` added in usage-alerts phase 0. The delivery consumer branches
// on `kind === 'email'` (phase 3) and invokes the Cloudflare Email
// Service binding instead of `fetch()`. HMAC signing is skipped — the
// recipient is CF Email Service, which already verifies us via SPF/DKIM.
export const WEBHOOK_ENDPOINT_KINDS = ['generic', 'slack', 'discord', 'email'] as const;
export type WebhookEndpointKind = (typeof WEBHOOK_ENDPOINT_KINDS)[number];

export const WEBHOOK_SIGNATURE_HEADER = 'X-Clickweave-Signature';
export const WEBHOOK_REPLAY_WINDOW_MS = 5 * 60 * 1000;
export const WEBHOOK_ROTATION_GRACE_MS = 24 * 60 * 60 * 1000;

export const WebhookEventEnvelopeSchema = z.object({
  id: z.string(),
  event: z.enum(WEBHOOK_EVENT_TYPES),
  occurred_at: z.string().datetime(),
  delivered_at: z.string().datetime(),
  attempt: z.number().int().nonnegative(),
  data: z.record(z.unknown()),
});
export type WebhookEventEnvelope = z.infer<typeof WebhookEventEnvelopeSchema>;

export const WebhookEventJobSchema = z.object({
  event_uuid: z.string(),
  organisation_id: z.number().int().positive(),
  event_type: z.string(),
});
export type WebhookEventJob = z.infer<typeof WebhookEventJobSchema>;

export const WebhookAttemptJobSchema = z.object({
  message_id: z.number().int().positive(),
  message_uuid: z.string(),
  endpoint_id: z.number().int().positive(),
  attempt: z.number().int().nonnegative(),
  reason: z.enum(['initial', 'retry', 'manual_replay']),
});
export type WebhookAttemptJob = z.infer<typeof WebhookAttemptJobSchema>;
